import axios from 'axios';
import { createAsyncThunk, isFulfilled, isPending } from '@reduxjs/toolkit';
import { cleanEntity } from '../../../../../app/shared/util/entity-utils';
import { IQueryParams, createEntitySlice, EntityState, serializeAxiosError } from '../../.../../../app/shared/reducers/reducer.utils';
import { defaultValue, ICaseReview } from '../../shared/model/case-review.model';

const initialState: EntityState<ICaseReview> = {
  loading: false,
  errorMessage: null,
  entities: [],
  entity: defaultValue,
  updating: false,
  totalItems: 0,
  updateSuccess: false,
  steps: [],
};

const apiUrl = 'api/case-reviews';
const chatApiUrl = 'api/workflows/4911/start';

// Actions

export const getEntities = createAsyncThunk(
  'caseReview/fetch_entity_list',
  async ({ page, size, sort, query }: IQueryParams) => {
    const requestUrl = `${apiUrl}?${sort ? `page=${page}&size=${size}&sort=${sort}&` : ''}cacheBuster=${new Date().getTime()}`;
    return axios.get<ICaseReview[]>(requestUrl);
  },
  { serializeError: serializeAxiosError },
);

export const getEntity = createAsyncThunk(
  'caseReview/fetch_entity',
  async (id: string | number) => {
    const requestUrl = `${apiUrl}/${id}`;
    return axios.get<ICaseReview>(requestUrl);
  },
  { serializeError: serializeAxiosError },
);

export const getAiChatId = createAsyncThunk(
  'caseReview/get_chatId',
  async (id: string | number) => {
    const requestUrl = `${chatApiUrl}?extRefKey=${id}`;
    // const requestUrl = `${chatApiUrl}`;

    const headers = {
      'Workflow-Authorization': 'Dyhac$25!',
    };
    const result = await axios.get(requestUrl, { headers });
    localStorage.setItem('ai_id', JSON.stringify(result.data?.data));
    return result;
  },
  { serializeError: serializeAxiosError },
);

export const createEntity = createAsyncThunk(
  'caseReview/create_entity',
  async (entity: ICaseReview, thunkAPI) => {
    const result = await axios.post<ICaseReview>(apiUrl, cleanEntity(entity));
    thunkAPI.dispatch(getEntities({}));
    return result;
  },
  { serializeError: serializeAxiosError },
);

export const updateEntity = createAsyncThunk(
  'caseReview/update_entity',
  async (entity: ICaseReview, thunkAPI) => {
    const result = await axios.put<ICaseReview>(`${apiUrl}/${entity.id}`, cleanEntity(entity));
    thunkAPI.dispatch(getEntities({}));
    return result;
  },
  { serializeError: serializeAxiosError },
);

export const partialUpdateEntity = createAsyncThunk(
  'caseReview/partial_update_entity',
  async (entity: ICaseReview, thunkAPI) => {
    const result = await axios.patch<ICaseReview>(`${apiUrl}/${entity.id}`, cleanEntity(entity));
    thunkAPI.dispatch(getEntities({}));
    return result;
  },
  { serializeError: serializeAxiosError },
);

export const deleteEntity = createAsyncThunk(
  'caseReview/delete_entity',
  async (id: string | number, thunkAPI) => {
    const requestUrl = `${apiUrl}/${id}`;
    const result = await axios.delete<ICaseReview>(requestUrl);
    thunkAPI.dispatch(getEntities({}));
    return result;
  },
  { serializeError: serializeAxiosError },
);

// slice

export const CaseReviewSlice = createEntitySlice({
  name: 'caseReview',
  initialState,
  extraReducers(builder) {
    builder
      .addCase(getEntity.fulfilled, (state, action) => {
        state.loading = false;
        state.entity = action.payload.data;
      })
      .addCase(getAiChatId.fulfilled, (state, action) => {
        state.loading = false;
        state.entity = action.payload?.data ?? null;
      })
      .addCase(getAiChatId.pending, (state, action) => {
        state.loading = true;
      })
      .addCase(getAiChatId.rejected, (state, action) => {
        state.loading = false;
      })
      .addCase(deleteEntity.fulfilled, state => {
        state.updating = false;
        state.updateSuccess = true;
        state.entity = {};
      })
      .addMatcher(isFulfilled(getEntities), (state, action) => {
        const { data, headers } = action.payload;

        return {
          ...state,
          loading: false,
          entities: data,
          totalItems: parseInt(headers['x-total-count'], 10),
        };
      })
      .addMatcher(isFulfilled(createEntity, updateEntity, partialUpdateEntity), (state, action) => {
        state.updating = false;
        state.loading = false;
        state.updateSuccess = true;
        state.entity = action.payload.data;
      })
      .addMatcher(isPending(getEntities, getEntity), state => {
        state.errorMessage = null;
        state.updateSuccess = false;
        state.loading = true;
      })
      .addMatcher(isPending(createEntity, updateEntity, partialUpdateEntity, deleteEntity), state => {
        state.errorMessage = null;
        state.updateSuccess = false;
        state.updating = true;
      });
  },
});

export const { reset } = CaseReviewSlice.actions;

// Reducer
export default CaseReviewSlice.reducer;
